import React, { useState } from 'react';
import { Code2, ChevronRight, Layers3, Palette, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { isDevelopmentBuild } from '../utils/buildEnvironment';

const gallerySections = [
  { id: 'theme', label: 'Theme & type' },
  { id: 'skeletons', label: 'Page skeletons' },
  { id: 'buttons', label: 'Buttons & variants' },
  { id: 'switches', label: 'Switches & toggles' },
  { id: 'tables', label: 'Tables' },
  { id: 'pagination', label: 'Pagination' },
  { id: 'sidebars', label: 'Sidebars' },
  { id: 'charts', label: 'Charts' },
  { id: 'overlays', label: 'Modals & popups' },
  { id: 'inventory', label: 'Component inventory' },
] as const;

const DevFloatingMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  if (!isDevelopmentBuild) return null;

  const openSection = (sectionId: string) => {
    setIsOpen(false);
    if (location.pathname !== '/dev/components') {
      navigate(`/dev/components#${sectionId}`);
      return;
    }

    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="fixed bottom-4 right-4 z-80 flex flex-col items-end gap-2">
      {isOpen && (
        <div className="w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-neon/25 bg-surface shadow-[0_24px_80px_rgba(0,0,0,0.65)]">
          <div className="flex items-center justify-between border-b border-white/10 bg-neon/6 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-neon/20 bg-neon/10 text-neon"><Code2 className="h-4 w-4" /></span>
              <div>
                <p className="text-xs font-semibold text-white">Developer tools</p>
                <p className="text-[9px] uppercase tracking-[0.14em] text-muted">Local development</p>
              </div>
            </div>
            <button type="button" onClick={() => setIsOpen(false)} className="icon-action flex h-7 w-7 items-center justify-center" aria-label="Close developer menu"><X className="h-3.5 w-3.5" /></button>
          </div>

          <button
            type="button"
            onClick={() => openSection('theme')}
            className="flex w-full items-center justify-between border-b border-white/10 px-4 py-3 text-left transition hover:bg-neon/[0.07]"
          >
            <span className="flex items-center gap-2 text-xs font-semibold text-neon"><Palette className="h-4 w-4" />Theme &amp; component gallery</span>
            <ChevronRight className="h-4 w-4 text-muted" />
          </button>

          <div className="max-h-[min(55vh,28rem)] overflow-y-auto p-2">
            <p className="px-2 pb-1 pt-1 text-[9px] font-bold uppercase tracking-[0.16em] text-muted">Jump to section</p>
            {gallerySections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => openSection(section.id)}
                className="group flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-[11px] text-white/70 transition hover:bg-white/5 hover:text-white"
              >
                <span className="flex items-center gap-2"><Layers3 className="h-3.5 w-3.5 text-muted group-hover:text-neon" />{section.label}</span>
                <ChevronRight className="h-3.5 w-3.5 text-muted/50 group-hover:text-neon" />
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close developer tools' : 'Open developer tools'}
        title="Developer tools"
        className="flex h-12 items-center gap-2 rounded-full border border-neon/40 bg-surface px-4 text-neon shadow-[0_8px_32px_rgba(0,0,0,0.5)] transition hover:border-neon/70 hover:bg-surface-2 hover:shadow-[0_0_24px_rgba(92,246,205,0.18)]"
      >
        {isOpen ? <X className="h-4 w-4" /> : <Code2 className="h-4 w-4" />}
        <span className="text-[10px] font-bold uppercase tracking-[0.16em]">Dev</span>
      </button>
    </div>
  );
};

export default DevFloatingMenu;
